import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { FavoriteControl } from "@/Common/Components/Favorites/FavoriteControl";
import { PinItemControl } from "@/Common/Components/IconControls/PinItemControl";
import { ISimpleDisplayModel } from "@/Common/Components/Search/SimpleSearchAutocomplete";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { RvqPreferenceType } from "./Constants/RvqPreferenceType";
import { BaseRvqPreference } from "./Models/BaseRvqPreference";
import { RvqPreferenceHelper } from "./Models/RvqPreferenceHelper";
import { GetRvqPreferencesResponse } from "./ResponseModels/GetRvqPreferencesResponse";

interface RvqAutoCompleteRowProps<TPreference extends BaseRvqPreference> {
    model: ISimpleDisplayModel<TPreference>;
    onRvqPreferencesUpdate: (preferences: TPreference[], newPreference?: TPreference) => void;
}

class RvqAutoCompleteRow<TPreference extends BaseRvqPreference> extends BaseReactComponent<RvqAutoCompleteRowProps<TPreference>, null> {
    public render(): JSX.Element {
        const model = this.props.model;

        const data: TPreference = { ...model.Data };
        return (
            <div className="immunization-dropdown-item" style={{ width: "100%" }}>
                <span>
                    <PinItemControl
                        onPinItemClick={isPinned => {
                            data.IsPinned = isPinned;
                            data.IsFavorite = isPinned || data.IsFavorite;
                            this.updatePreference(data);
                        }}
                        isPinned={data.IsPinned}
                    />
                </span>
                <span>
                    <FavoriteControl
                        isFavoriteSet={model.Data.IsFavorite}
                        onChange={isFavorite => {
                            if (!data.IsPinned)
                                data.IsFavorite = isFavorite;

                            this.updatePreference(data);
                        }}
                        imageStyles={{ height: "20px" }}
                    />
                </span>
                <span className="ellipsis-auto" style={{ width: "100%" }}>{model.DisplayText}</span>
            </div>
        );
    }

    private async updatePreference(preference: TPreference): Promise<void> {
        // get property name
        const isNew = !preference.PreferenceId;
        const propertyName = this.getPropertyName(preference.Type);
        const returnPropertyName = this.getReturnPropertyName(preference.Type);
        if (!propertyName || !returnPropertyName) {
            console.error(`Can't update preference for unhandled type '${preference.Type}'.`);
            return;
        }

        // submit update
        const response = await Client.postAsync<GetRvqPreferencesResponse>(
            isNew
                ? ServerOperations.Server.QuebecVaccinationRegistry_AddPreference
                : ServerOperations.Server.QuebecVaccinationRegistry_UpdatePreference,
            {
                [propertyName]: preference
            }
        );

        // update model
        if (response.isSuccess) {
            const updatedPreferences: TPreference[] = response?.data
                ?.[returnPropertyName]
                ?.map(p => RvqPreferenceHelper.BuildPreference<TPreference>(p, preference.Type));

            const newPreference = isNew
                ? updatedPreferences.firstOrDefault(x => x.comparableRvqId === RvqPreferenceHelper.BuildPreference<TPreference>(preference, preference.Type)?.comparableRvqId)
                : null;

            this.props.onRvqPreferencesUpdate(updatedPreferences, newPreference);
        }
    }

    /**
     * Get the name of the backend model property used to submit an updated preference, if any.
     * @param type The preference type.
     */
    private getPropertyName(type: RvqPreferenceType): string | null {
        switch (type) {
            case RvqPreferenceType.Location:
                return "LocationPreference";
            case RvqPreferenceType.Medication:
                return "MedicationPreference";
            case RvqPreferenceType.Practitioner:
                return "PractitionerPreference";
            default:
                return null;
        }
    }

    /**
     * Get the property name for updated preferences returned by the backend, if any.
     * @param type The preference type.
     */
    private getReturnPropertyName(type: RvqPreferenceType): string | null {
        switch (type) {
            case RvqPreferenceType.Location:
                return "LocationPreferences";
            case RvqPreferenceType.Medication:
                return "MedicationPreferences";
            case RvqPreferenceType.Practitioner:
                return "PractitionerPreferences";
            default:
                return null;
        }
    }
}

export { RvqAutoCompleteRow };
